import { useEffect, useState, type ReactNode } from 'react';
import { createRootRouteWithContext, Link, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { useMe } from '../lib/api/auth';
import { useMediaQuery } from '../lib/useMediaQuery';

interface RouterContext {
  queryClient: QueryClient;
}

function ErrorPage({ error }: { error: unknown }) {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    <div style={{ padding: 32, fontFamily: 'monospace', color: 'red' }}>
      <strong>App error:</strong> {msg}
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: ({ error }) => <ErrorPage error={error} />,
});

// ── Logo mark ─────────────────────────────────────────────────────────────────
function LogoMark() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{
        width: 22, height: 22, borderRadius: 6,
        background: 'linear-gradient(135deg, oklch(62% .18 250) 0%, oklch(58% .17 295) 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,.2), 0 1px 2px rgba(10,14,20,.15)',
        color: 'white', fontFamily: 'var(--lca-font-display)',
        fontWeight: 700, fontSize: 12, letterSpacing: '-0.04em', flexShrink: 0,
      }}>C</div>
      <div style={{
        fontFamily: 'var(--lca-font-display)', fontWeight: 600,
        fontSize: 15, letterSpacing: '-0.018em', color: 'var(--lca-ink)',
      }}>
        ChatTrust.AI<span style={{ color: 'var(--lca-gray-500)', fontWeight: 500 }}> Administration</span>
      </div>
    </div>
  );
}

// ── Section label ─────────────────────────────────────────────────────────────
function SectionLabel({ children }: { children: string }) {
  return (
    <div style={{
      marginTop: 14, marginBottom: 4, padding: '0 10px',
      fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em',
      color: 'var(--lca-gray-500)', fontWeight: 600,
    }}>{children}</div>
  );
}

// ── Sidebar icon (inline SVG, subset used in nav) ────────────────────────────
function NavIcon({ name }: { name: string }) {
  const paths: Record<string, ReactNode> = {
    grid:   <><rect x="2" y="2" width="5" height="5" rx="1"/><rect x="9" y="2" width="5" height="5" rx="1"/><rect x="2" y="9" width="5" height="5" rx="1"/><rect x="9" y="9" width="5" height="5" rx="1"/></>,
    chat:   <path d="M2 4.5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H7l-3 2.5v-2.5H4a2 2 0 0 1-2-2z"/>,
    users:  <><circle cx="6" cy="5.5" r="2.2"/><path d="M1.5 13c.4-2 2.2-3.3 4.5-3.3S10 11 10.4 13"/><path d="M10.2 5.5a2 2 0 0 1 0 4"/><path d="M11 9.7c1.8.2 3 1.4 3.4 3.3"/></>,
    user:   <><circle cx="8" cy="5.5" r="2.5"/><path d="M2.5 13.5c.5-2.4 2.8-4 5.5-4s5 1.6 5.5 4"/></>,
    shield: <path d="M8 1.5L2.5 3.5v4c0 3.4 2.4 6.2 5.5 7 3.1-.8 5.5-3.6 5.5-7v-4z"/>,
    key:    <><circle cx="5" cy="11" r="2.5"/><path d="M6.8 9.2L13 3M11 5l1.5 1.5M9.5 6.5L11 8"/></>,
    coin:   <><circle cx="8" cy="8" r="6"/><path d="M8 4.5v7M10 6.5c-.6-.8-1.4-1-2-1-1 0-2 .6-2 1.5s.8 1.2 2 1.5c1.3.3 2 .6 2 1.6 0 1-1 1.5-2 1.5-.7 0-1.5-.2-2-1"/></>,
    clock:  <><circle cx="8" cy="8" r="6"/><path d="M8 4.5V8l2.5 1.5"/></>,
    gear:   <><circle cx="8" cy="8" r="2.2"/><path d="M8 1.5v1M8 13.5v1M1.5 8h1M13.5 8h1M3.4 3.4l.7.7M11.9 11.9l.7.7M11.9 4.1l-.7.7M4.1 11.9l-.7.7"/></>,
    server: <><rect x="2" y="3" width="12" height="4" rx="1"/><rect x="2" y="9" width="12" height="4" rx="1"/><circle cx="12" cy="5" r=".7" fill="currentColor"/><circle cx="12" cy="11" r=".7" fill="currentColor"/></>,
  };
  return (
    <svg
      width={14} height={14} viewBox="0 0 16 16"
      fill="none" stroke="currentColor" strokeWidth={1.5}
      strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0, display: 'block' }}
    >
      {paths[name] ?? paths['grid']}
    </svg>
  );
}

// ── Nav item ──────────────────────────────────────────────────────────────────
const navBase = {
  display: 'flex', alignItems: 'center', gap: 10, height: 30,
  padding: '0 10px', borderRadius: 7,
  background: 'transparent', boxShadow: 'none',
  color: 'var(--lca-gray-700)', fontSize: 13, fontWeight: 450,
  letterSpacing: '-0.005em', textDecoration: 'none',
} as const;

const navActive = {
  display: 'flex', alignItems: 'center', gap: 10, height: 30,
  padding: '0 10px', borderRadius: 7,
  background: 'var(--lca-surface)',
  boxShadow: '0 0 0 1px var(--lca-hairline), 0 1px 1px rgba(10,14,20,.04)',
  color: 'var(--lca-ink)', fontSize: 13, fontWeight: 500,
  letterSpacing: '-0.005em', textDecoration: 'none',
} as const;

function NavItem({ to, label, icon, exact }: { to: string; label: string; icon: string; exact?: boolean }) {
  return (
    <Link
      to={to}
      activeOptions={exact ? { exact: true } : undefined}
      style={navBase}
      activeProps={{ style: navActive }}
    >
      <NavIcon name={icon} />
      <span style={{ flex: 1 }}>{label}</span>
    </Link>
  );
}

// ── Root layout ───────────────────────────────────────────────────────────────
function RootLayout() {
  const { isLoading, isError } = useMe();
  const router = useRouter();
  const pathname = useRouterState({ select: s => s.location.pathname });

  useEffect(() => {
    if (isError && pathname !== '/login') {
      void router.navigate({ to: '/login' });
    }
  }, [isError, pathname, router]);

  if (pathname === '/login') {
    return <Outlet />;
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', color: 'var(--lca-gray-500)', fontSize: 13 }}>
        Loading…
      </div>
    );
  }

  if (isError) {
    return null;
  }

  return <ResponsiveShell pathname={pathname} />;
}

function ResponsiveShell({ pathname }: { pathname: string }) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [navOpen, setNavOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setNavOpen(false); }, [pathname]);

  // Close on Escape
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  const sidebarInline = !isMobile;
  const sidebarVisible = sidebarInline || navOpen;

  return (
    <div style={{
      display: 'flex', height: '100vh',
      background: 'var(--lca-paper)', color: 'var(--lca-ink)',
      fontFamily: 'var(--lca-font-text)', overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Mobile hamburger — appears only when sidebar is hidden */}
      {isMobile && !navOpen && (
        <button
          type="button"
          aria-label="Open navigation"
          onClick={() => setNavOpen(true)}
          style={{
            position: 'fixed', top: 12, left: 12, zIndex: 30,
            width: 44, height: 44, borderRadius: 10,
            border: '1px solid var(--lca-hairline)', background: 'var(--lca-surface)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', boxShadow: '0 1px 3px rgba(10,14,20,.08)',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <path d="M3 4h10M3 8h10M3 12h10" />
          </svg>
        </button>
      )}

      {/* Backdrop for mobile drawer */}
      {isMobile && navOpen && (
        <div
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(10,14,20,0.45)',
            zIndex: 40, animation: 'fadeIn .15s ease',
          }}
        />
      )}

      <aside style={{
        width: 232, flexShrink: 0,
        background: 'rgba(245,245,247,0.92)',
        backdropFilter: 'blur(40px) saturate(180%)',
        WebkitBackdropFilter: 'blur(40px) saturate(180%)',
        borderRight: '1px solid var(--lca-hairline)',
        display: sidebarVisible ? 'flex' : 'none',
        flexDirection: 'column',
        height: '100%',
        ...(isMobile ? {
          position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 50,
          boxShadow: '4px 0 24px rgba(10,14,20,0.15)',
        } : {}),
      }}>
        <div style={{ padding: '18px 16px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <LogoMark />
          {isMobile && (
            <button
              type="button"
              aria-label="Close navigation"
              onClick={() => setNavOpen(false)}
              style={{
                width: 32, height: 32, borderRadius: 8, border: 'none',
                background: 'transparent', cursor: 'pointer', color: 'var(--lca-gray-700)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          )}
        </div>

        <nav style={{
          flex: 1, padding: '8px 12px',
          display: 'flex', flexDirection: 'column', gap: 1,
          overflowY: 'auto',
        }}>
          <NavItem to="/"              label="Overview"      icon="grid"   exact />
          <NavItem to="/conversations" label="Conversations" icon="chat" />
          <NavItem to="/users"         label="Users"         icon="users" />
        </nav>
      </aside>

      <main style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
        <Outlet />
      </main>
    </div>
  );
}
